import { Injectable } from '@angular/core';
import { englishLanguageLocale } from '../constants-types/constants';
import {
    GlobalEmailTypes,
    FactorCombinationType,
    LanguageAvailabilityByProduct,
    AssessmentType
} from '../constants-types/Enums';
import { PI } from '../constants-types/PI.model';
import { ConsoleLogger } from '../core-utilities/ConsoleLogger';
import { Util } from '../core-utilities/Util';
import { Language } from '../shared/Language.model';
import { LanguagesApi } from './LanguagesApi/LanguagesApi.connect';
import { LanguagesTransformers } from './LanguagesApi/LanguagesApi.datatransform';
import { TranslationDataService } from './Translation.dataservice';

/*
This is an injectable service that serves as a wrapper around a synchronously
loaded translation files. This is intended to read from objected loaded via
script tags before the application is running.
*/

(window as any).Translations = (window as any).Translations || {};
declare let Translations: any; // Done to expose global variable to this service

type LanguageByProduct = {
    [product: number]: Language[];
};

type TranslationMode = 'Normal' | 'Hash' | 'Enclose' | 'Key';

@Injectable({
    providedIn: 'root'
})
export class TranslationCoreService {
    specialTranslation: {
        [key: string]: {
            [key: string]: string;
        };
    } = {};

    languageByProduct: LanguageByProduct = {};
    translationModeOn = false;
    translationMode: TranslationMode;

    allMissingStrings: any = {};

    constructor(
        private languagesApi: LanguagesApi,
        private languageTransform: LanguagesTransformers,
        private util: Util,
        private translations: TranslationDataService
    ) {
        this.translationModeOn = PI.appSettings.translationModeOn;

        // Initialize the map with enum value as key instead of string name
        this.languageByProduct = {};
        if (PI.productLanguageMap) {
            Object.keys(PI.productLanguageMap).forEach((key) => {
                const enumKey =
                    key as keyof typeof LanguageAvailabilityByProduct;
                const enumValue = LanguageAvailabilityByProduct[enumKey];
                this.languageByProduct[enumValue] = PI.productLanguageMap[
                    enumKey
                ]
                    .map((l) => this.languageTransform.transformLanguage(l))
                    .sort(this.languageComparison);
            });
        }
    }

    get currentLocale() {
        return PI.languageLocale;
    }

    get behavioralLanguages() {
        return this.getLanguagesFor(
            LanguageAvailabilityByProduct.BehavioralAssessment
        );
    }
    get cognitiveLanguages() {
        return this.getLanguagesFor(LanguageAvailabilityByProduct.Cognitive);
    }

    // Should probably make this a decorator
    // Should also probably AllowAnonymous on language APIs
    get fullSiteLanguages() {
        return this.getLanguagesFor(LanguageAvailabilityByProduct.FullSite);
    }

    getLanguagesFor(product: LanguageAvailabilityByProduct): Language[] {
        return this.languageByProduct[product];
    }

    private languageComparison = (langA: Language, langB: Language) => {
        if (langA.display > langB.display) {
            return 1;
        } else if (langB.display > langA.display) {
            return -1;
        } else {
            return 0;
        }
    };

    getCurrentLanguage(product: LanguageAvailabilityByProduct): Language {
        const currentLanguage = this.languageByProduct[product].find(
            (lang) => lang.locale === this.currentLocale
        );
        return currentLanguage || new Language();
    }

    getLanguagebyAssessmentType(
        product: AssessmentType,
        locale: string
    ): Language {
        switch (product) {
            case AssessmentType.Behavioral:
            case AssessmentType.BulkAssessmentUpload:
                return this.findLanguage(
                    locale,
                    LanguageAvailabilityByProduct.BehavioralAssessment
                );
            case AssessmentType.Cognitive:
                return this.findLanguage(
                    locale,
                    LanguageAvailabilityByProduct.Cognitive
                );
            case AssessmentType.Job:
                return this.findLanguage(
                    locale,
                    LanguageAvailabilityByProduct.JobAssessment
                );
            // explicitly calling out Strategy Assessment as it is a major assessment type but only has one langauge currently.
            case AssessmentType.StrategyAssessment:
                return new Language({ locale: 'en-US' });
            default:
                return new Language({ locale: 'en-US' });
        }
    }

    findLanguage(
        locale: string,
        product = LanguageAvailabilityByProduct.BehavioralAssessment
    ): Language {
        return this.languageByProduct[product].find(
            (language) => language.locale === locale
        );
    }

    get fullSiteLanguagesList() {
        return this.languagesApi.getLanguageList();
    }

    allowGenderNeutralReports(
        languageCode: string = this.currentLocale
    ): boolean {
        return this.getLanguagesFor(
            LanguageAvailabilityByProduct.GenderNeutralReports
        ).some((m) => m.locale === languageCode);
    }

    // Note that this has some translation difficulties. 'And' may be different word
    // based on what is in list (i.e. 'a' versus 'an')

    // However, this is nicely unit testable!

    formWrittenList(words: string[]) {
        if (words.length === 0) {
            return '';
        }
        if (words.length === 1) {
            return words[0];
        }
        if (words.length === 2) {
            return `${words[0]} ${this.key('And')} ${words[1]}`;
        }
        const allbutLast = words.slice(0, words.length - 1).join(', ');
        return `${allbutLast}, ${this.key('And')} ${words[words.length - 1]}`;
    }

    key(keyToTranslate: string, languageCode?: string): string {
        switch (this.translationMode) {
            case 'Hash':
                return '#'.repeat(
                    this.figureTranslation(keyToTranslate, languageCode).length
                );
            case 'Enclose':
                return `#${keyToTranslate}#`;
            case 'Key':
                return keyToTranslate;
        }
        return this.figureTranslation(keyToTranslate, languageCode);
    }

    private figureTranslation(
        keyToTranslate: string,
        languageCode?: string
    ): string {
        if (!keyToTranslate) {
            return '';
        }
        const lowerKey = keyToTranslate
            ? keyToTranslate.toLowerCase()
            : keyToTranslate;
        let language = languageCode;
        if (!language) {
            language = this.currentLocale;
        }

        if (
            language &&
            this.specialTranslation[language] &&
            this.specialTranslation[language][lowerKey]
        ) {
            return this.specialTranslation[language][lowerKey];
        }

        let translation = this.translations.data[lowerKey];

        // adding this check because it doesn't appear that the toLower match
        // is always working since the key names are not always lowercased
        // REMOVE ONCE FIXED
        if (!translation) {
            translation = this.translations.data[keyToTranslate];
        }

        if (translation) {
            return translation;
        }
        if (
            PI.appSettings.logMissingTranslationKeys &&
            !this.allMissingStrings[keyToTranslate]
        ) {
            this.allMissingStrings[keyToTranslate] = true;
            ConsoleLogger.log(`Missing key: ${keyToTranslate}`);
        }
        return `##${keyToTranslate}##`;
    }
    /*
    public setLanguageInformation(languages, cognitiveLanguages, currentLocale) {
        this.languages = languages;
        this.cognitiveLanguages = cognitiveLanguages;
        this.currentLanguage = this.languages.find((lang: Language) => {
            return lang.locale === currentLocale;
        })
    }
*/
    fillInKeys(
        keysToTranslate: string[],
        languageCode: string
    ): Promise<any[]> {
        if (!this.specialTranslation[languageCode]) {
            this.specialTranslation[languageCode] = {};
        }

        // check for case where keys exist
        const keysAlreadyStored = keysToTranslate.every(
            (key) => this.specialTranslation[languageCode][key] !== undefined
        );
        if (keysAlreadyStored) {
            return Promise.resolve([]); // return an empty array to match return type below
        }
        // TODO Code below seems redundant with code further below - take a look
        return this.languagesApi
            .fillInKeys(keysToTranslate, languageCode)
            .then((translations) => {
                translations.forEach((entry) => {
                    this.specialTranslation[languageCode][entry.keyName] =
                        entry.translationValue;
                });
                return translations;
            });
    }

    getEmailSubjectMessageOverrides(
        keysToTranslate: string[],
        languageCode: string,
        companyId: string,
        emailType: GlobalEmailTypes
    ) {
        return this.languagesApi
            .getEmailSubjectMessageOverrides(
                keysToTranslate,
                languageCode,
                companyId,
                emailType
            )
            .then((translations) => {
                translations.forEach((entry) => {
                    if (!this.specialTranslation[languageCode]) {
                        this.specialTranslation[languageCode] = {};
                    }
                    this.specialTranslation[languageCode][entry.keyName] =
                        entry.translationValue;
                });
                return translations;
            });
    }

    // We need to update everyone to use the currentLocaleService but this will work in meantime
    isEnglish() {
        return this.currentLocale === englishLanguageLocale;
    }

    isOnetDataSupported() {
        return this.isEnglish();
    }

    getDefaultBehavioralReportLanguage(): Language {
        const language = this.behavioralLanguages.find(
            (lang) => lang.locale === this.currentLocale
        );
        return language ? language : new Language();
    }

    getDefaultCognitiveReportLanguage(): Language {
        const language = this.cognitiveLanguages.find(
            (lang) => lang.locale === this.currentLocale
        );
        return language ? language : new Language();
    }

    getFactorCombinationTranslation(combo: FactorCombinationType): string {
        let factor1: string;
        let factor2: string;
        switch (combo) {
            case FactorCombinationType.Unknown:
                return this.key('common.unkown');
            // Equal factors cases
            // NOTE: these shouldn't need to be translated because the factor
            // letters should not be translated and '=' is a language agnostic
            // symbol
            case FactorCombinationType.AEqualB:
                return 'A = B';
            case FactorCombinationType.AEqualC:
                return 'A = C';
            case FactorCombinationType.AEqualD:
                return 'A = D';
            case FactorCombinationType.BEqualC:
                return 'B = C';
            case FactorCombinationType.BEqualD:
                return 'B = D';
            case FactorCombinationType.CEqualD:
                return 'C = D';

            // "{0} over {1}" factor cases
            // These *do* need to be translated
            case FactorCombinationType.AOverB:
                factor1 = 'A';
                factor2 = 'B';
                break;
            case FactorCombinationType.BOverA:
                factor1 = 'B';
                factor2 = 'A';
                break;
            case FactorCombinationType.AOverC:
                factor1 = 'A';
                factor2 = 'C';
                break;
            case FactorCombinationType.COverA:
                factor1 = 'C';
                factor2 = 'A';
                break;
            case FactorCombinationType.AOverD:
                factor1 = 'A';
                factor2 = 'D';
                break;
            case FactorCombinationType.DOverA:
                factor1 = 'D';
                factor2 = 'A';
                break;
            case FactorCombinationType.BOverC:
                factor1 = 'B';
                factor2 = 'C';
                break;
            case FactorCombinationType.COverB:
                factor1 = 'C';
                factor2 = 'B';
                break;
            case FactorCombinationType.BOverD:
                factor1 = 'B';
                factor2 = 'D';
                break;
            case FactorCombinationType.DOverB:
                factor1 = 'D';
                factor2 = 'B';
                break;
            case FactorCombinationType.COverD:
                factor1 = 'C';
                factor2 = 'D';
                break;
            case FactorCombinationType.DOverC:
                factor1 = 'D';
                factor2 = 'C';
                break;
        }
        const translation = this.key('common.factorOverFactor');
        return this.util.strings.formatString(translation, factor1, factor2);
    }

    notImplementedKey(key: string): string {
        // placeholder method for development purposes where a translation is not yet available.  Allows for easy find/replace once translations are avialable.
        return key;
    }
}
